import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { usePermissions, MODULES, ACTIONS, USER_ROLES } from "../hooks/usePermissions";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import ThemeToggleButton from "../helper/ThemeToggleButton";
import IconButton from "../helper/IconButton";
import NotificationDropdown from "../components/notifications/NotificationDropdown";
import NotificationPreferencesModal from "../components/notifications/NotificationPreferencesModal";
import NotificationContainer from "../components/notifications/NotificationContainer";
import { useMenuState } from "../hooks/useMenuState";
import MenuItemWithSubmenu from "../components/common/MenuItemWithSubmenu";
import AffiliateInviteBanner from "../components/affiliate/AffiliateInviteBanner";
import "../components/common/DashboardMenuItem.css";

const MasterLayout = ({ children }) => {
  let [sidebarActive, seSidebarActive] = useState(false);
  let [mobileMenu, setMobileMenu] = useState(false);
  const [showNotificationPreferences, setShowNotificationPreferences] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    // Recuperar estado do localStorage
    const saved = localStorage.getItem('pagmus-sidebar-collapsed');
    return saved ? JSON.parse(saved) : false;
  });
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
  const [userAvatar, setUserAvatar] = useState(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('user_data')) || JSON.parse(localStorage.getItem('user')) || {};
      return userData.avatar_url || userData.avatar || null;
    } catch {
      return null;
    }
  });
  const [userName, setUserName] = useState(() => {
    try {
      const userData = JSON.parse(localStorage.getItem('user_data')) || JSON.parse(localStorage.getItem('user')) || {};
      return userData.name || 'U';
    } catch {
      return 'U';
    }
  });
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user, isAuthenticated, loading } = useAuth();
  const { hasPermission, canAccessRoute, isOwner, isAdmin, isCustomer, isEmployee, isSeller } = usePermissions();
  const { menuStructure, openMenus, toggleMenu, isRouteActive, isMenuActive } = useMenuState();

  // ========================================
  // 🎯 LÓGICA DE ACESSO DIFERENCIADO POR TIPO DE USUÁRIO
  // ========================================
  
  // 1. ADMIN: Acesso APENAS à seção Administrador
  const isAdminUser = isAdmin();
  
  // 2. CUSTOMER: Acesso APENAS à seção de Compras
  const isCustomerUser = isCustomer();
  
  // 3. OWNER e FUNCIONÁRIOS: Acesso completo às funcionalidades da plataforma
  const isOwnerOrEmployee = isOwner() || isEmployee();
  
  // 4. SELLERS (Vendedores/Afiliados Diretos): Acesso restrito
  const isSellerUser = isSeller();
  
  // Flags de visibilidade baseadas no tipo de usuário
  // Sellers VEEM vendas (mas filtradas apenas para suas vendas)
  const showSalesSection = (() => {
    if (isOwner()) return true;
    if (isSellerUser) return true; // Sellers sempre veem Vendas
    if (isOwnerOrEmployee && hasPermission(MODULES.VENDAS, ACTIONS.VIEW)) return true;
    return false;
  })();
  
  // Sellers VEEM produtos (tela específica "Produtos")
  const showProductsSection = (() => {
    if (isOwner()) return true;
    if (isSellerUser) return true; // Sellers sempre veem Produtos
    if (isOwnerOrEmployee && (
      hasPermission(MODULES.PRODUTOS, ACTIONS.CREATE) ||
      hasPermission(MODULES.PRODUTOS, ACTIONS.VIEW) ||
      hasPermission(MODULES.AFILIADOS, ACTIONS.VIEW)
    )) return true;
    return false;
  })();

  // Sellers NÃO VEEM afiliados (não gerenciam afiliados)
  const showAffiliatesSection = isOwnerOrEmployee && !isSellerUser && (
    isOwner() ||
    hasPermission(MODULES.AFILIADOS, ACTIONS.MANAGE) ||
    hasPermission(MODULES.AFILIADOS, ACTIONS.VIEW)
  );

  // Sellers VEEM financeiro (saques, contas bancárias, responsáveis)
  const showFinanceSection = (() => {
    if (isOwner()) return true;
    if (isSellerUser) return true; // Sellers sempre veem Financeiro
    if (isOwnerOrEmployee && hasPermission(MODULES.FINANCEIRO, ACTIONS.VIEW)) return true;
    return false;
  })();

  // Sellers NÃO VEEM relatórios gerais (apenas suas vendas)
  const showReportsSection = isOwnerOrEmployee && !isSellerUser && (
    isOwner() ||
    hasPermission(MODULES.RELATORIOS, ACTIONS.VIEW)
  );

  // Seção Acessos: OWNER ou colaboradores com permissão (Sellers NÃO veem)
  const showAccessSection = isOwner() || (
    isOwnerOrEmployee && !isSellerUser && (
      hasPermission(MODULES.USUARIOS, ACTIONS.VIEW) || 
      hasPermission(MODULES.USUARIOS, ACTIONS.CREATE)
    )
  );

  // Seção de Compras: APENAS para CUSTOMER
  const showPurchasesSection = isCustomerUser;

  // Seção Administrador: APENAS para ADMIN (NUNCA para OWNER)
  const showAdminSection = isAdminUser;
  
  // Seção Loja: BLOQUEADO para grupos e sellers (apenas OWNER tem acesso)
  // Grupos e sellers não devem ter acesso à Loja
  const showStoreSection = isOwner() && !isSellerUser;
  
  // Seção Operações: APENAS para OWNER (Tickets agora está em Ajuda)
  const showOperationsSection = isOwner();
  
  // Filtrar submenus de Operações
  // OWNER vê tudo (exceto Fretes que está oculto temporariamente)
  const getFilteredOperacoesMenu = () => {
    if (!menuStructure.operacoes) return menuStructure.operacoes;
    return menuStructure.operacoes;
  };

  // Filtrar submenus de Produtos baseado em permissões
  // OWNER vê tudo EXCETO /products/seller, sellers veem apenas "Produtos"
  const getFilteredProdutosMenu = () => {
    if (!menuStructure.produtos) return menuStructure.produtos;
    
    if (isSellerUser) {
      // Sellers veem apenas a tela "Produtos"
      return {
        ...menuStructure.produtos,
        submenus: menuStructure.produtos.submenus.filter(submenu => 
          submenu.path === '/products/seller'
        ),
        routes: ['/products/seller']
      };
    }
    
    if (isOwner()) {
      // OWNER vê tudo, MAS não vê /products/seller (exclusivo para sellers)
      return {
        ...menuStructure.produtos,
        submenus: menuStructure.produtos.submenus.filter(submenu => 
          submenu.path !== '/products/seller'
        )
      };
    }
    
    // Colaboradores veem conforme permissões (filtrado pelo MenuItemWithSubmenu)
    return menuStructure.produtos;
  };

  // Filtrar submenus de Vendas baseado em permissões
  // Sellers veem apenas: Afterpay (kanban e relatórios) e Relatório de Vendas
  const getFilteredVendasMenu = () => {
    if (!menuStructure.vendas) return menuStructure.vendas;
    
    if (isOwner()) {
      // OWNER vê tudo
      return menuStructure.vendas;
    }
    
    if (isSellerUser) {
      // Sellers veem apenas: Afterpay, Relatório de Vendas
      return {
        ...menuStructure.vendas,
        submenus: menuStructure.vendas.submenus.filter(submenu => 
          submenu.path === '/afterpay' ||
          submenu.path === '/sales-management'
        ),
        routes: ['/afterpay', '/sales-management']
      };
    }
    
    // Colaboradores veem conforme permissões
    return menuStructure.vendas;
  };

  // Filtrar submenus de Financeiro baseado em permissões
  // Sellers veem: Saques, Contas Bancárias, Responsáveis
  const getFilteredFinanceiroMenu = () => {
    if (!menuStructure.financeiro) return menuStructure.financeiro;
    
    if (isOwner()) {
      // OWNER vê tudo
      return menuStructure.financeiro;
    }
    
    if (isSellerUser) {
      // Sellers veem: Saques, Contas Bancárias, Responsáveis (já está correto)
      return menuStructure.financeiro;
    }
    
    // Colaboradores veem conforme permissões
    return menuStructure.financeiro;
  };

  // Filtrar submenus de Ajuda baseado em permissões
  // Sellers veem apenas Tickets (não Wiki)
  const getFilteredAjudaMenu = () => {
    if (!menuStructure.ajuda) return menuStructure.ajuda;
    
    if (isOwner()) {
      // OWNER vê tudo
      return menuStructure.ajuda;
    }
    
    if (isSellerUser) {
      // Sellers veem apenas Tickets
      return {
        ...menuStructure.ajuda,
        submenus: menuStructure.ajuda.submenus.filter(submenu => 
          submenu.path === '/tickets'
        ),
        routes: ['/tickets']
      };
    }
    
    // Colaboradores veem conforme permissões
    return menuStructure.ajuda;
  };

  useEffect(() => {
    let isMounted = true;
    
    const handleDropdownClick = (event) => {
      // Verificar se o componente ainda está montado
      if (!isMounted) return;
      
      // Verificar se o elemento ainda existe no DOM
      if (!event.currentTarget || !event.currentTarget.closest) {
        return;
      }
      
      event.preventDefault();
      const clickedLink = event.currentTarget;
      const clickedDropdown = clickedLink.closest(".dropdown");

      if (!clickedDropdown) return;

      const isCurrentlyOpen = clickedDropdown.classList.contains("open");

      // Fechar todos os outros dropdowns primeiro
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        if (dropdown !== clickedDropdown && dropdown.parentNode && isMounted) {
          dropdown.classList.remove("open");
          const submenu = dropdown.querySelector(".sidebar-submenu");
          if (submenu && submenu.parentNode) {
            submenu.style.maxHeight = "0px";
          }
        }
      });

      // Toggle o dropdown clicado
      if (isCurrentlyOpen) {
        clickedDropdown.classList.remove("open");
        const submenu = clickedDropdown.querySelector(".sidebar-submenu");
        if (submenu && submenu.parentNode) {
          submenu.style.maxHeight = "0px";
        }
      } else {
        clickedDropdown.classList.add("open");
        const submenu = clickedDropdown.querySelector(".sidebar-submenu");
        if (submenu && submenu.parentNode) {
          submenu.style.maxHeight = `${submenu.scrollHeight}px`;
        }
      }
    };

    // Attach click event listeners to all dropdown triggers
    const dropdownTriggers = document.querySelectorAll(
      ".sidebar-menu .dropdown > a, .sidebar-menu .dropdown > Link"
    );

    dropdownTriggers.forEach((trigger) => {
      if (trigger.parentNode) {
        trigger.addEventListener("click", handleDropdownClick);
      }
    });

    const openActiveDropdown = () => {
      if (!isMounted) return;
      
      // Usar requestAnimationFrame para garantir que o DOM esteja estável
      requestAnimationFrame(() => {
        if (!isMounted) return;
        
        const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
        
        // Primeiro fechar todos
        allDropdowns.forEach((dropdown) => {
          if (dropdown.parentNode && isMounted && dropdown.isConnected) {
            dropdown.classList.remove("open");
            const submenu = dropdown.querySelector(".sidebar-submenu");
            if (submenu && submenu.parentNode && submenu.isConnected) {
              submenu.style.maxHeight = "0px";
            }
          }
        });
        
        // Depois abrir o que tem página ativa
        allDropdowns.forEach((dropdown) => {
          if (!isMounted || !dropdown.isConnected) return;
          
          const submenuLinks = dropdown.querySelectorAll(".sidebar-submenu li a");
          let hasActiveLink = false;
          
          submenuLinks.forEach((link) => {
            if (link.parentNode && link.isConnected) {
              const linkPath = link.getAttribute("to") || link.getAttribute("href");
              if (linkPath === location.pathname) {
                hasActiveLink = true;
              }
            }
          });
          
          if (hasActiveLink && dropdown.parentNode && dropdown.isConnected) {
            dropdown.classList.add("open");
            const submenu = dropdown.querySelector(".sidebar-submenu");
            if (submenu && submenu.parentNode && submenu.isConnected && isMounted) {
              // Use requestAnimationFrame para garantir que o cálculo da altura seja feito corretamente
              requestAnimationFrame(() => {
                if (isMounted && submenu.parentNode && submenu.isConnected) {
                  submenu.style.maxHeight = `${submenu.scrollHeight}px`;
                }
              });
            }
          }
        });
      });
    };

    // Open the submenu that contains the active route
    openActiveDropdown();

    // Cleanup event listeners on unmount
    return () => {
      isMounted = false;
      dropdownTriggers.forEach((trigger) => {
        if (trigger && trigger.parentNode) {
          trigger.removeEventListener("click", handleDropdownClick);
        }
      });
    };
  }, [location.pathname]);

  // Observer para mudanças de tema
  useEffect(() => {
    const handleThemeChange = () => {
      const currentTheme = document.documentElement.getAttribute('data-theme') || 'light';
      setTheme(currentTheme);
    };

    const observer = new MutationObserver(handleThemeChange);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    handleThemeChange();
    return () => observer.disconnect();
  }, []);

  // Observer para mudanças no localStorage (avatar e nome) e no user do AuthContext
  useEffect(() => {
    const updateUserData = () => {
      try {
        // IMPORTANTE: Priorizar localStorage se user do AuthContext não tiver avatar_url
        const localUserData = JSON.parse(localStorage.getItem('user_data') || localStorage.getItem('user') || '{}');
        const userAvatarUrl = user?.avatar_url || user?.avatar || null;
        const localAvatarUrl = localUserData?.avatar_url || localUserData?.avatar || null;
        
        // Usar avatar_url do user se existir, senão usar do localStorage
        const avatarUrl = userAvatarUrl || localAvatarUrl || null;
        const name = user?.name || localUserData?.name || 'U';
        
        console.log('🔄 MasterLayout - Atualizando dados do usuário:', {
          hasUser: !!user,
          userId: user?.id,
          userAvatarUrl: userAvatarUrl,
          localStorageAvatarUrl: localAvatarUrl,
          finalAvatarUrl: avatarUrl,
          name: name,
          currentUserAvatar: userAvatar,
          willChange: userAvatar !== avatarUrl
        });
        
        if (avatarUrl !== userAvatar) {
          console.log('✅ MasterLayout - Avatar mudou de', userAvatar, 'para', avatarUrl);
          setUserAvatar(avatarUrl);
        } else {
          console.log('⏭️ MasterLayout - Avatar não mudou, mantendo:', avatarUrl);
        }
        
        if (name !== userName) {
          setUserName(name);
        }
      } catch (error) {
        console.error('❌ MasterLayout - Erro ao atualizar dados do usuário:', error);
        setUserAvatar(null);
        setUserName('U');
      }
    };

    // Atualizar ao montar e quando user mudar
    console.log('🔄 MasterLayout - useEffect executado, user:', user?.id, 'avatar_url:', user?.avatar_url);
    updateUserData();

    // Observar mudanças no localStorage (de outras abas)
    const handleStorageChange = (e) => {
      if (e.key === 'user_data' || e.key === 'user') {
        console.log('🔄 MasterLayout - localStorage mudou:', e.key);
        updateUserData();
      }
    };

    // Listener para evento customizado de atualização de avatar
    const handleAvatarUpdate = () => {
      console.log('🔄 MasterLayout - Evento userAvatarUpdated disparado');
      updateUserData();
    };

    // Listener para mudanças no localStorage (de outras abas)
    window.addEventListener('storage', handleStorageChange);
    
    // Listener para evento customizado (mesma aba)
    window.addEventListener('userAvatarUpdated', handleAvatarUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userAvatarUpdated', handleAvatarUpdate);
    };
  }, [user, userAvatar, userName]); // Depender do user do AuthContext e estados atuais

  let sidebarControl = () => {
    seSidebarActive(!sidebarActive);
  };

  let mobileMenuControl = () => {
    setMobileMenu(!mobileMenu);
  };

  const handleLogout = () => {
    console.log('🚪 Iniciando logout...');
    
    // Logout via AuthContext (limpa estado e localStorage)
    logout();
    
    // Limpar localStorage manualmente para garantir
    localStorage.removeItem('auth_token');
    localStorage.removeItem('token');
    localStorage.removeItem('user_data');
    
    console.log('🧹 Dados limpos, redirecionando...');
    
    // Forçar redirecionamento
    window.location.href = '/';
  };

  return (
    <>
      {/* Tarja de funcionário no topo */}
      {(() => {
        try {
          const userData = JSON.parse(localStorage.getItem('user_data')) || JSON.parse(localStorage.getItem('user'));
          if (userData?.role === 'funcionario' && userData?.owner_info) {
            return (
              <div className="employee-banner" style={{
                backgroundColor: '#343a40',
                borderBottom: '1px solid #495057',
                padding: '12px 20px',
                textAlign: 'center',
                fontSize: '14px',
                color: '#ffffff',
                fontWeight: '500',
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                width: '100%',
                zIndex: 9999,
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                Você está operando na conta de <strong>{userData.owner_info.name}</strong> ({userData.owner_info.email})
              </div>
            );
          }
          return null;
        } catch(_) { 
          return null; 
        }
      })()}

      {/* CSS para a nova sidebar */}
      <style>{`
        ${(() => {
          try {
            const userData = JSON.parse(localStorage.getItem('user_data')) || JSON.parse(localStorage.getItem('user'));
            if (userData?.role === 'funcionario' && userData?.owner_info) {
              return `
                .sidebar {
                  padding-top: 50px !important;
                }
                .dashboard-main {
                  padding-top: 50px !important;
                }
              `;
            }
            return '';
          } catch(_) { 
            return ''; 
          }
        })()}
        .sidebar-section-header {
          padding: 8px 24px 2px 24px;
          margin-top: 16px;
          border-bottom: none;
          position: relative;
        }
        
        .sidebar-section-header:first-of-type {
          margin-top: 6px;
        }
        
        .sidebar-section-header:not(:first-of-type)::before {
          content: '';
          position: absolute;
          top: -8px;
          left: 24px;
          right: 24px;
          height: 1px;
          background-color: #e2e8f0;
        }
        
        .sidebar-section-header span {
          font-size: 9px;
          font-weight: 600;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .submenu-icon {
          width: 14px;
          height: 14px;
          margin-right: 6px;
          color: #64748b;
        }
        
        .sidebar-submenu li a {
          display: flex;
          align-items: center;
          padding: 4px 24px 4px 42px;
          font-size: 13px;
          line-height: 1.1;
        }
        
        .sidebar-submenu li a.active-page .submenu-icon {
          color: #0d6efd;
        }
        
        .sidebar-menu > li:not(.sidebar-section-header) {
          margin-bottom: 4px;
        }
        
        /* Dashboard e outros itens sem submenu - padding uniforme */
        .sidebar-menu > li > a {
          padding: 12px 16px;
          font-size: 14px;
          line-height: 1.2;
          border-radius: 6px;
          margin: 0 4px;
          display: flex;
          align-items: center;
          transition: all 0.2s ease;
        }
        
        /* Dashboard quando não está ativo - manter mesmo tamanho */
        .sidebar-menu > li > a:not(.active-page) {
          padding: 12px 16px;
        }
        
        .sidebar-menu .dropdown > a .menu-icon {
          font-size: 16px;
        }
        
        .sidebar-submenu {
          padding-top: 2px;
          padding-bottom: 2px;
        }

        /* Separador explícito entre blocos quando necessário */
        .sidebar-section-separator {
          height: 1px;
          margin: 8px 24px;
          background-color: #e2e8f0;
          list-style: none;
        }

        /* Dashboard ativo com mesmo estilo do submenu - MESMO TAMANHO */
        .sidebar-menu > li > a.active-page {
          background-color: var(--bs-primary) !important;
          color: white !important;
          font-weight: 500 !important;
          box-shadow: 0 2px 4px rgba(var(--bs-primary-rgb), 0.3) !important;
          border-radius: 6px !important;
          margin: 0 4px !important;
          padding: 12px 16px !important; /* MESMO padding para não mudar tamanho */
        }

        .sidebar-menu > li > a.active-page .menu-icon {
          color: white !important;
        }

        /* Dark mode - Dashboard ativo azul */
        [data-theme="dark"] .sidebar-menu > li > a.active-page {
          background-color: #4dabf7 !important;
          color: #ffffff !important;
          box-shadow: 0 2px 8px rgba(77, 171, 247, 0.4) !important;
        }

        [data-theme="dark"] .sidebar-menu > li > a.active-page .menu-icon {
          color: #ffffff !important;
        }

        /* ========================================
           DARK MODE - LAYOUT STRUCTURE IMPROVEMENTS
           ======================================== */
        
        /* Dark mode - Sidebar background */
        [data-theme="dark"] .sidebar {
          background-color: #1a1a1a !important;
          border-right: 1px solid #4a4a4a !important;
        }
        
        /* Dark mode - Navbar background */
        [data-theme="dark"] .navbar-header {
          background-color: var(--dark-bg-primary) !important;
          border-bottom: 1px solid #4a4a4a !important;
        }
        
        /* Dark mode - Content area background (darker) */
        [data-theme="dark"] .dashboard-main {
          background-color: #1f1f1f !important;
        }
        
        [data-theme="dark"] .dashboard-main-body {
          background-color: #141313 !important;
        }
        
        /* Dark mode - Sidebar separators (more gray, less blue) */
        [data-theme="dark"] .sidebar-section-header:not(:first-of-type)::before {
          background-color: #4a4a4a !important;
        }
        
        [data-theme="dark"] .sidebar-section-separator {
          background-color: #4a4a4a !important;
        }
        
        /* Dark mode - Sidebar text colors */
        [data-theme="dark"] .sidebar-section-header span {
          color: #9ca3af !important;
        }
        
        [data-theme="dark"] .submenu-icon {
          color: #9ca3af !important;
        }
        
        /* Dark mode - Navbar text colors */
        [data-theme="dark"] .navbar-header span {
          color: #f9fafb !important;
        }
        
        /* Dark mode - Sidebar menu items */
        [data-theme="dark"] .sidebar-menu > li > a {
          color: #d1d5db !important;
        }
        
        [data-theme="dark"] .sidebar-menu > li > a:hover {
          background-color: #374151 !important;
          color: #ffffff !important;
        }
        
        [data-theme="dark"] .sidebar-submenu li a {
          color: #9ca3af !important;
        }
        
        [data-theme="dark"] .sidebar-submenu li a:hover {
          background-color: #374151 !important;
          color: #ffffff !important;
        }
        
        /* Dark mode - Remove black background from Dashboard */
        [data-theme="dark"] .dashboard-main-body {
          background-color: #141313 !important;
        }
        
        /* Dark mode - Remove container-fluid black background */
        [data-theme="dark"] .container-fluid {
          background-color: transparent !important;
        }
        
        /* Dark mode - Remove any black backgrounds from Dashboard cards */
        [data-theme="dark"] .dashboard-main .card,
        [data-theme="dark"] .dashboard-main .card-body,
        [data-theme="dark"] .dashboard-main .bg-white {
          background-color: transparent !important;
        }
        
        /* Dark mode - Ensure Dashboard content has proper background */
        [data-theme="dark"] .dashboard-main .card,
        [data-theme="dark"] .dashboard-main .card-body {
          background-color: transparent !important;
        }
        
        /* ========================================
           SIDEBAR COLLAPSED - VERSÃO RECOLHIDA
           ======================================== */
        
        /* Botão de toggle collapse no HEADER */
        .sidebar-toggle-header-btn {
          background: transparent;
          border: none;
          color: var(--bs-body-color);
          cursor: pointer;
          padding: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 6px;
          transition: all 0.2s ease;
          font-size: 20px;
        }
        
        /* Esconder botão de collapse em telas menores que 1200px */
        @media (max-width: 1199px) {
          .sidebar-toggle-header-btn {
            display: none !important;
          }
        }
        
        .sidebar-toggle-header-btn:hover {
          background-color: rgba(var(--bs-primary-rgb), 0.1);
          color: var(--bs-primary);
        }
        
        [data-theme="dark"] .sidebar-toggle-header-btn {
          color: #f9fafb;
        }
        
        [data-theme="dark"] .sidebar-toggle-header-btn:hover {
          background-color: rgba(255, 255, 255, 0.1);
          color: #74c0fc;
        }
        
        /* Sidebar collapsed */
        .sidebar.sidebar-collapsed {
          width: 70px !important;
        }
        
        .sidebar.sidebar-collapsed .sidebar-logo img.light-logo,
        .sidebar.sidebar-collapsed .sidebar-logo img.dark-logo {
          display: none;
        }
        
        .sidebar.sidebar-collapsed .sidebar-logo img.logo-icon {
          display: block;
          width: 40px;
          height: 40px;
        }
        
        /* Menu collapsed - esconder labels */
        .sidebar-menu-collapsed .menu-label,
        .sidebar-menu-collapsed .sidebar-menu > li > a > span:not(.menu-icon) {
          display: none !important;
        }
        
        .sidebar-menu-collapsed .menu-icon {
          margin-right: 0 !important;
        }
        
        /* Menu items collapsed - centralizar */
        .sidebar-menu-collapsed .sidebar-menu > li > a {
          justify-content: center;
          align-items: center;
          padding: 12px !important;
        }
        
        /* Dashboard collapsed - ícone centralizado PERFEITAMENTE */
        .sidebar-menu-collapsed .sidebar-menu > li {
          display: flex !important;
          justify-content: center !important;
        }
        
        .sidebar-menu-collapsed .sidebar-menu > li > a {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          padding: 12px !important;
          position: relative;
          width: 100% !important;
          margin: 0 !important;
        }
        
        .sidebar-menu-collapsed .sidebar-menu > li > a .menu-icon {
          margin: 0 !important;
          margin-right: 0 !important;
          margin-left: 0 !important;
          display: inline-block !important;
          width: auto !important;
          height: auto !important;
          flex: none !important;
          position: static !important;
        }
        
        .sidebar-menu-collapsed .sidebar-menu > li > a.active-page .menu-icon {
          margin: 0 !important;
          margin-right: 0 !important;
          margin-left: 0 !important;
          position: static !important;
        }
        
        /* Remover qualquer span que possa estar desalinhando */
        .sidebar-menu-collapsed .sidebar-menu > li > a > span:not(.menu-icon) {
          display: none !important;
        }
        
        /* Tooltip para ícones quando collapsed */
        .sidebar-menu-collapsed .sidebar-menu > li > a[title]:hover::after,
        .sidebar-menu-collapsed .menu-header[title]:hover::after {
          content: attr(title);
          position: fixed;
          left: 70px;
          background-color: #1a1a1a;
          color: white;
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 12px;
          white-space: nowrap;
          z-index: 10001;
          margin-left: 8px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          pointer-events: none;
          top: var(--tooltip-top, auto);
          transform: translateY(-50%);
        }
        
        [data-theme="dark"] .sidebar-menu-collapsed .sidebar-menu > li > a[title]:hover::after,
        [data-theme="dark"] .sidebar-menu-collapsed .menu-header[title]:hover::after {
          background-color: #2a2a2a;
          color: #f9fafb;
        }
        
        /* Permitir clique no menu header quando collapsed para abrir submenu */
        .sidebar-menu-collapsed .menu-header {
          cursor: pointer;
        }
        
        .sidebar-menu-collapsed .menu-header:hover {
          background-color: rgba(var(--bs-primary-rgb), 0.1);
        }
        
        .sidebar-menu-collapsed .menu-header {
          justify-content: center;
          align-items: center;
          padding: 12px !important;
        }
        
        .sidebar-menu-collapsed .menu-header .menu-icon {
          margin: 0 !important;
        }
        
        .sidebar-menu-collapsed .menu-toggle-icon {
          display: none;
        }
        
        /* Dashboard ativo quando collapsed - melhor visual */
        .sidebar-menu-collapsed .sidebar-menu > li > a.active-page {
          background-color: var(--bs-primary) !important;
          color: white !important;
          border-radius: 8px !important;
          box-shadow: 0 2px 8px rgba(var(--bs-primary-rgb), 0.4) !important;
        }
        
        [data-theme="dark"] .sidebar-menu-collapsed .sidebar-menu > li > a.active-page {
          background-color: #4dabf7 !important;
          box-shadow: 0 2px 12px rgba(77, 171, 247, 0.5) !important;
        }
        
        /* Menu header ativo quando collapsed */
        .sidebar-menu-collapsed .menu-header.active-menu {
          background-color: var(--bs-primary) !important;
          color: white !important;
          border-radius: 8px !important;
          box-shadow: 0 2px 8px rgba(var(--bs-primary-rgb), 0.4) !important;
        }
        
        [data-theme="dark"] .sidebar-menu-collapsed .menu-header.active-menu {
          background-color: #4dabf7 !important;
          box-shadow: 0 2px 12px rgba(77, 171, 247, 0.5) !important;
        }
        
        /* Submenus suspensos no hover quando collapsed */
        .sidebar-menu-collapsed .menu-item-with-submenu {
          position: relative;
        }
        
        .sidebar-menu-collapsed .submenu-container {
          position: fixed !important;
          left: 70px !important;
          top: var(--submenu-top, auto) !important;
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          z-index: 10000 !important; /* Acima do conteúdo e header */
          min-width: 200px;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.2s ease, visibility 0.2s ease;
          margin-left: 8px;
          max-height: 500px;
          overflow-y: auto;
          padding-left: 0 !important;
        }
        
        /* Remover linha azul vertical quando collapsed */
        .sidebar-menu-collapsed .submenu-list {
          border-left: none !important;
          margin-left: 0 !important;
          padding-left: 0 !important;
          padding-top: 12px !important;
        }
        
        /* Adicionar seta apontando para o menu originário */
        .sidebar-menu-collapsed .submenu-container::before {
          content: '';
          position: absolute;
          left: -8px;
          top: 20px;
          width: 0;
          height: 0;
          border-top: 8px solid transparent;
          border-bottom: 8px solid transparent;
          border-right: 8px solid white;
          z-index: 1;
        }
        
        [data-theme="dark"] .sidebar-menu-collapsed .submenu-container::before {
          border-right-color: #1a1a1a;
        }
        
        .sidebar-menu-collapsed .menu-item-with-submenu:hover .submenu-container {
          opacity: 1 !important;
          visibility: visible !important;
        }
        
        /* Dark mode - Submenus suspensos */
        [data-theme="dark"] .sidebar-menu-collapsed .submenu-container {
          background-color: #1a1a1a;
          border: 1px solid #4a4a4a;
        }
        
        /* ========================================
           AJUSTE DO CONTEÚDO QUANDO SIDEBAR COLAPSA
           ======================================== */
        
        /* Sidebar normal */
        .sidebar {
          transition: width 0.3s ease;
        }
        
        /* Dashboard main ajusta quando sidebar colapsa */
        .dashboard-main {
          transition: margin-inline-start 0.3s ease, margin-left 0.3s ease, width 0.3s ease;
        }
        
        /* Quando sidebar colapsa, o conteúdo se expande */
        /* Usar tanto margin-inline-start quanto margin-left para compatibilidade */
        body:has(.sidebar-collapsed) .dashboard-main,
        .dashboard-main.sidebar-collapsed {
          margin-inline-start: 70px !important;
          margin-left: 70px !important;
        }
        
        /* Sobrescrever media queries quando collapsed */
        @media (min-width: 1200px) {
          body:has(.sidebar-collapsed) .dashboard-main,
          .dashboard-main.sidebar-collapsed {
            margin-inline-start: 70px !important;
            margin-left: 70px !important;
          }
        }
        
        @media (min-width: 1400px) {
          body:has(.sidebar-collapsed) .dashboard-main,
          .dashboard-main.sidebar-collapsed {
            margin-inline-start: 70px !important;
            margin-left: 70px !important;
          }
        }
        
        @media (min-width: 1650px) {
          body:has(.sidebar-collapsed) .dashboard-main,
          .dashboard-main.sidebar-collapsed {
            margin-inline-start: 70px !important;
            margin-left: 70px !important;
          }
        }
        
        /* Ajuste para mobile - não aplicar quando sidebar está aberta no mobile */
        @media (max-width: 768px) {
          body:has(.sidebar-collapsed) .dashboard-main,
          .dashboard-main.sidebar-collapsed {
            margin-inline-start: 0 !important;
            margin-left: 0 !important;
          }
        }
      `}</style>
      
      <section className={mobileMenu ? "overlay active" : "overlay "}>
        {/* sidebar */}
        <aside
        className={
          mobileMenu
            ? "sidebar sidebar-open"
            : sidebarCollapsed
            ? "sidebar sidebar-collapsed"
            : "sidebar"
        }
      >
        <button
          onClick={mobileMenuControl}
          type="button"
          className="sidebar-close-btn"
        >
          <Icon icon="solar:close-circle-bold" />
        </button>
        <div>
          <Link to={user?.role === 'admin' ? '/admin/dashboard' : '/dashboard'} className="sidebar-logo">
            <img
              src="/assets/images/logo.png"
              alt="site logo"
              className="light-logo"
            />
            <img
              src="/assets/images/logo-light.png"
              alt="site logo"
              className="dark-logo"
            />
            <img
              src="/assets/images/logo-icon.png"
              alt="site logo"
              className="logo-icon"
            />
          </Link>
        </div>
        <div className="sidebar-menu-area">
          <ul className={`sidebar-menu ${sidebarCollapsed ? 'sidebar-menu-collapsed' : ''}`} id="sidebar-menu">
            {/* Dashboard */}
            <li className="dashboard-menu-item">
              <NavLink
                to={isAdminUser ? "/admin/dashboard" : "/dashboard"}
                className={(navData) => {
                  const baseClass = "dashboard-link";
                  return navData.isActive ? `${baseClass} active-page` : baseClass;
                }}
                title={sidebarCollapsed ? "Dashboard" : ""}
                onMouseEnter={(e) => {
                  if (sidebarCollapsed) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    e.currentTarget.style.setProperty('--tooltip-top', `${rect.top}px`);
                  }
                }}
              >
                <Icon icon="solar:home-smile-angle-outline" className="dashboard-icon" />
                <span className="dashboard-label">Dashboard</span>
              </NavLink>
            </li>

            {/* Seção: Produtos */}
            {showProductsSection && (
              <MenuItemWithSubmenu
                menuKey="produtos"
                menuData={getFilteredProdutosMenu()}
                isOpen={openMenus.produtos}
                isActive={isMenuActive('produtos')}
                onToggle={toggleMenu}
                isRouteActive={isRouteActive}
                hasPermission={showProductsSection}
              />
            )}

            {/* Seção: Vendas */}
            {showSalesSection && (
              <MenuItemWithSubmenu
                menuKey="vendas"
                menuData={getFilteredVendasMenu()}
                isOpen={openMenus.vendas}
                isActive={isMenuActive('vendas')}
                onToggle={toggleMenu}
                isRouteActive={isRouteActive}
                hasPermission={showSalesSection}
              />
            )}

            {/* Seção: Loja - APENAS para OWNER e FUNCIONÁRIOS */}
            {showStoreSection && (
              <MenuItemWithSubmenu
                menuKey="loja"
                menuData={menuStructure.loja}
                isOpen={openMenus.loja}
                isActive={isMenuActive('loja')}
                onToggle={toggleMenu}
                isRouteActive={isRouteActive}
                hasPermission={showStoreSection}
              />
            )}

            {/* Seção: Compras - APENAS para CUSTOMER */}
            {showPurchasesSection && (
              <MenuItemWithSubmenu
                menuKey="compras"
                menuData={menuStructure.compras}
                isOpen={openMenus.compras}
                isActive={isMenuActive('compras')}
                onToggle={toggleMenu}
                isRouteActive={isRouteActive}
                hasPermission={showPurchasesSection}
              />
            )}

            {/* Seção: Afiliados */}
            {showAffiliatesSection && (
              <MenuItemWithSubmenu
                menuKey="afiliados"
                menuData={menuStructure.afiliados}
                isOpen={openMenus.afiliados}
                isActive={isMenuActive('afiliados')}
                onToggle={toggleMenu}
                isRouteActive={isRouteActive}
                hasPermission={showAffiliatesSection}
              />
            )}



            {/* Seção: Financeiro */}
            {showFinanceSection && (
              <MenuItemWithSubmenu
                menuKey="financeiro"
                menuData={getFilteredFinanceiroMenu()}
                isOpen={openMenus.financeiro}
                isActive={isMenuActive('financeiro')}
                onToggle={toggleMenu}
                isRouteActive={isRouteActive}
                hasPermission={showFinanceSection}
              />
            )}

            {/* Seção: Relatórios */}
            {showReportsSection && (
              <MenuItemWithSubmenu
                  menuKey="relatorios"
                  menuData={menuStructure.relatorios}
                  isOpen={openMenus.relatorios}
                  isActive={isMenuActive('relatorios')}
                  onToggle={toggleMenu}
                  isRouteActive={isRouteActive}
                  hasPermission={showReportsSection}
                />
            )}

            {/* Seções Administrativas - Menus Separados */}
            {showAdminSection && (
              <>
                {/* Vendas */}
                <MenuItemWithSubmenu
                  menuKey="adminVendas"
                  menuData={menuStructure.adminVendas}
                  isOpen={openMenus.adminVendas}
                  isActive={isMenuActive('adminVendas')}
                  onToggle={toggleMenu}
                  isRouteActive={isRouteActive}
                  hasPermission={showAdminSection}
                />
                
                {/* Financeiro */}
                <MenuItemWithSubmenu
                  menuKey="adminFinanceiro"
                  menuData={menuStructure.adminFinanceiro}
                  isOpen={openMenus.adminFinanceiro}
                  isActive={isMenuActive('adminFinanceiro')}
                  onToggle={toggleMenu}
                  isRouteActive={isRouteActive}
                  hasPermission={showAdminSection}
                />
                
                {/* Aprovações */}
                <MenuItemWithSubmenu
                  menuKey="adminAprovacoes"
                  menuData={menuStructure.adminAprovacoes}
                  isOpen={openMenus.adminAprovacoes}
                  isActive={isMenuActive('adminAprovacoes')}
                  onToggle={toggleMenu}
                  isRouteActive={isRouteActive}
                  hasPermission={showAdminSection}
                />
                
                {/* Suporte */}
                <MenuItemWithSubmenu
                  menuKey="adminSuporte"
                  menuData={menuStructure.adminSuporte}
                  isOpen={openMenus.adminSuporte}
                  isActive={isMenuActive('adminSuporte')}
                  onToggle={toggleMenu}
                  isRouteActive={isRouteActive}
                  hasPermission={showAdminSection}
                />
                
                {/* Usuários */}
                <MenuItemWithSubmenu
                  menuKey="adminUsuarios"
                  menuData={menuStructure.adminUsuarios}
                  isOpen={openMenus.adminUsuarios}
                  isActive={isMenuActive('adminUsuarios')}
                  onToggle={toggleMenu}
                  isRouteActive={isRouteActive}
                  hasPermission={showAdminSection}
                />
                
                {/* Configurações */}
                <MenuItemWithSubmenu
                  menuKey="adminConfiguracoes"
                  menuData={menuStructure.adminConfiguracoes}
                  isOpen={openMenus.adminConfiguracoes}
                  isActive={isMenuActive('adminConfiguracoes')}
                  onToggle={toggleMenu}
                  isRouteActive={isRouteActive}
                  hasPermission={showAdminSection}
                />
              </>
            )}

            {/* Seção: Acessos - APENAS para OWNER */}
            {showAccessSection && (
              <MenuItemWithSubmenu
                menuKey="acessos"
                menuData={menuStructure.acessos}
                isOpen={openMenus.acessos}
                isActive={isMenuActive('acessos')}
                onToggle={toggleMenu}
                isRouteActive={isRouteActive}
                hasPermission={showAccessSection}
              />
            )}

            {/* Seção: Operações - OWNER (total) ou Colaboradores (apenas Tickets) */}
            {showOperationsSection && (
              <MenuItemWithSubmenu
                menuKey="operacoes"
                menuData={getFilteredOperacoesMenu()}
                isOpen={openMenus.operacoes}
                isActive={isMenuActive('operacoes')}
                onToggle={toggleMenu}
                isRouteActive={isRouteActive}
                hasPermission={showOperationsSection}
              />
            )}

            {/* Seção: Ajuda - DISPONÍVEL PARA TODOS (OWNER, Colaboradores e Sellers) */}
            {isAuthenticated && (
              <MenuItemWithSubmenu
                menuKey="ajuda"
                menuData={getFilteredAjudaMenu()}
                isOpen={openMenus.ajuda}
                isActive={isMenuActive('ajuda')}
                onToggle={toggleMenu}
                isRouteActive={isRouteActive}
                hasPermission={true}
              />
            )}
          </ul>
        </div>
      </aside>

      <main className={`dashboard-main ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        <div className="navbar-header">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-4">
                {/* Botão mobile menu */}
                <button onClick={mobileMenuControl} type="button" className="sidebar-mobile-toggle">
                  <Icon icon="solar:hamburger-menu-bold" className="icon" />
                </button>
                
                {/* Botão de collapse/expand sidebar - NO HEADER */}
                <button
                  onClick={() => {
                    const newState = !sidebarCollapsed;
                    setSidebarCollapsed(newState);
                    localStorage.setItem('pagmus-sidebar-collapsed', JSON.stringify(newState));
                  }}
                  className="sidebar-toggle-header-btn"
                  title={sidebarCollapsed ? "Expandir menu" : "Recolher menu"}
                >
                  <Icon icon={sidebarCollapsed ? "solar:sidebar-minimalistic-bold" : "solar:alt-arrow-left-bold"} />
                </button>
                
                {/* Saudação personalizada - Alinhada à esquerda */}
                <div className="d-flex flex-column align-items-start">
                  <span style={{ 
                    fontSize: '16px', 
                    fontWeight: '600', 
                    color: 'var(--dark-text-primary, #1a1a1a)',
                    lineHeight: '1.2'
                  }}>
                    Olá, {(() => { try { return (JSON.parse(localStorage.getItem('user_data'))?.name) || (JSON.parse(localStorage.getItem('user'))?.name) || 'Usuário'; } catch(_) { return 'Usuário'; }})()}
                  </span>
                  <span style={{ 
                    fontSize: '12px', 
                    fontWeight: '400', 
                    color: 'var(--dark-text-secondary, #6b7280)',
                    lineHeight: '1.3'
                  }}>
                    Bem-vindo a Pagmus! 🚀
                  </span>
                </div>
              </div>
            </div>
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-3">
                {/* Theme Toggle - Padronizado */}
                <ThemeToggleButton />
                
                {/* Configurações de Notificações - Padronizado */}
                <IconButton
                  icon="solar:settings-bold"
                  title="Configurar notificações"
                  onClick={() => setShowNotificationPreferences(true)}
                />

                {/* Welcome - Navegar para página */}
                <IconButton
                  icon="solar:hand-heart-bold"
                  title="Boas-vindas"
                  onClick={() => navigate('/welcome')}
                />

                {/* Notificações - Padronizado */}
                <NotificationDropdown />

                {/* Avatar - Inicial do Usuário com Gradiente Premium */}
                <div className="dropdown">
                  <button
                    className="d-flex justify-content-center align-items-center rounded-circle"
                    type="button"
                    data-bs-toggle="dropdown"
                    style={{
                      width: '40px',
                      height: '40px',
                      padding: '0',
                      border: 'none',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      fontSize: '16px',
                      fontWeight: '700',
                      color: '#ffffff',
                      boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)';
                      e.currentTarget.style.transform = 'scale(1.08)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                    }}
                  >
                    {userAvatar ? (
                      <img
                        src={userAvatar}
                        alt="Avatar"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '50%'
                        }}
                        onError={(e) => {
                          // Se a imagem falhar, mostrar inicial
                          e.target.style.display = 'none';
                          const initial = userName.charAt(0).toUpperCase();
                          e.target.parentElement.textContent = initial;
                        }}
                      />
                    ) : (
                      userName.charAt(0).toUpperCase()
                    )}
                  </button>
                  <div className="dropdown-menu to-top dropdown-menu-sm">
                    <div className="py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2">
                      <div>
                        <h6 className="text-lg text-primary-light fw-semibold mb-2">
                          {(() => { try { return (JSON.parse(localStorage.getItem('user_data'))?.name) || (JSON.parse(localStorage.getItem('user'))?.name) || 'Usuário'; } catch(_) { return 'Usuário'; }})()}
                        </h6>
                        <p className="text-sm text-primary-light mb-0">
                          {(() => { try { return (JSON.parse(localStorage.getItem('user_data'))?.email) || (JSON.parse(localStorage.getItem('user'))?.email) || 'email@exemplo.com'; } catch(_) { return 'email@exemplo.com'; }})()}
                        </p>
                        {/* Disclaimer para funcionários */}
                        {(() => {
                          try {
                            const userData = JSON.parse(localStorage.getItem('user_data')) || JSON.parse(localStorage.getItem('user'));
                            if (userData?.role === 'funcionario' && userData?.owner_info) {
                              return (
                                <div className="mt-3 p-3 rounded-3" style={{
                                  backgroundColor: '#f8f9fa',
                                  border: '1px solid #e9ecef'
                                }}>
                                  <div className="d-flex align-items-center mb-2">
                                    <Icon icon="solar:user-speak-bold" className="me-2 text-primary" style={{fontSize: '16px'}} />
                                    <span className="text-xs text-muted fw-medium">OPERANDO COMO</span>
                                  </div>
                                  <p className="text-sm mb-1 fw-semibold text-dark">
                                    {userData.owner_info.name}
                                  </p>
                                  <p className="text-xs text-muted mb-0">
                                    {userData.owner_info.email}
                                  </p>
                                </div>
                              );
                            }
                            return null;
                          } catch(_) { 
                            return null; 
                          }
                        })()}
                      </div>
                    </div>
                    <ul className="to-top-list">
                      <li>
                        <Link className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3" to="/view-profile">
                          <Icon icon="solar:user-bold" className="icon text-xl" /> Meus Dados
                        </Link>
                      </li>
                      <li>
                        <Link className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3" to="#" onClick={handleLogout}>
                          <Icon icon="solar:logout-2-bold" className="icon text-xl" /> Sair do sistema
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        {/* Banner de Convites de Afiliado */}
        <AffiliateInviteBanner />

        <div className="dashboard-main-body">{children}</div>
      </main>
    </section>
    
    {/* Container de Notificações Toast */}
    <NotificationContainer position="top-end" />
    
    {/* Modal de Preferências de Notificações */}
    <NotificationPreferencesModal
      show={showNotificationPreferences}
      onHide={() => setShowNotificationPreferences(false)}
    />
    </>
  );
};


export default MasterLayout;
